import axios from 'axios'
import { toast } from '@/hooks/use-toast'
import WarningRoundedIcon from '@/components/ui/icons/warning-rounded'
import { cn } from './utils'

export enum UserConnectionStatus {
  BE_FAIL = 'BE_FAIL', // Backend failure
  GEN_FAIL = 'GEN_FAIL', // Generic internet failure
  GOOD = 'GOOD' // Everything is working
}

export const connectionStatusLabelMap = {
  [UserConnectionStatus.BE_FAIL]: 'We’re currently experiencing technical difficulties. Please stand by.',
  [UserConnectionStatus.GEN_FAIL]: 'Your internet connection appears to be slow or unstable. Please wait.',
  [UserConnectionStatus.GOOD]: 'Everything looks good. Please be patient while loading.'
}

async function isGenericInternetFailure() {
  try {
    const resp = await axios.get<{
      Answer: {
        name: string
      }[]
    }>('https://dns.google/resolve?name=google.com&type=A', { timeout: 2000 })

    return !resp.data?.Answer?.some(answer => answer.name.indexOf('google.com') > -1)
  } catch (error) {
    console.warn('GEN Failure', error)
    return true
  }
}

async function isBEFailure() {
  try {
    await axios.get<{
      db: string
      tp: string
      api: string
    }>('/status', { timeout: 2000, baseURL: import.meta.env.VITE_NODE_API_URL })
    return false
  } catch (error) {
    console.warn('BE Failure', error)
    return true
  }
}

export async function getConnectionStatus(): Promise<UserConnectionStatus> {
  // First check if there's a generic internet failure.
  if (!window.navigator.onLine || (await isGenericInternetFailure())) {
    return UserConnectionStatus.GEN_FAIL
  }

  // Then check if the BE is failing.
  if (await isBEFailure()) {
    return UserConnectionStatus.BE_FAIL
  }

  return UserConnectionStatus.GOOD
}

const RETRY_TOAST_DURATION = 15 * 1000 // This is an important notice, should give it 15s of display

export async function displayConnectionStatusToast() {
  const connectionStatus = await getConnectionStatus()
  const connectionStatusLabel = connectionStatusLabelMap[connectionStatus]
  toast({
    id: 'connection-status',
    description: (
      <div className='flex items-center gap-2'>
        <WarningRoundedIcon />
        <p className={cn('font-medium', connectionStatus !== UserConnectionStatus.GOOD && 'text-red-500')}>
          {connectionStatusLabel}
        </p>
      </div>
    ),
    duration: RETRY_TOAST_DURATION
  })
}
